import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { UserPicker } from '../common';

const CreateOrganizationModal = ({ users, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
    auto_approval: false,
    admin_user_ids: []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Only call the onCreate function which handles the API call
      // Remove the direct axios call to prevent duplicate creation
      await onCreate(formData);
      onClose();
    } catch (error) {
      console.error('Organization creation error:', error.response?.status, error.response?.data?.detail || error.message);
      alert('Failed to create organization: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAdminToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      admin_user_ids: prev.admin_user_ids.includes(userId)
        ? prev.admin_user_ids.filter(id => id !== userId)
        : [...prev.admin_user_ids, userId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-ui-surface">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-ui-ink">Create New Organization</h3>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 text-xl"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">
                Organization Name <span className="text-ui-red-fg">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                required
                placeholder="Enter organization name..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                rows="3"
                placeholder="Optional description..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">
                Self-Onboarding Domain
              </label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                placeholder="e.g., company.com (users with @company.com can self-onboard)"
              />
              <p className="text-xs text-ui-muted mt-1">
                Users with this email domain can automatically request to join this organization
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="auto_approval"
                checked={formData.auto_approval}
                onChange={(e) => setFormData(prev => ({ ...prev, auto_approval: e.target.checked }))}
                className="h-4 w-4 text-ui-link border-ui-control rounded"
              />
              <label htmlFor="auto_approval" className="ml-2 text-sm text-ui-ink-2">
                Auto-approve domain self-onboarding (otherwise requires admin approval)
              </label>
            </div>

            <div>
              <p className="text-xs text-ui-ink-3 mb-3">
                Select existing users from any organization to become admins of this new organization. They will be transferred to this organization with admin privileges.
              </p>
              <UserPicker
                users={users.map(u => ({
                  ...u,
                  name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
                }))}
                selectedIds={formData.admin_user_ids}
                onChange={(ids) => setFormData(prev => ({ ...prev, admin_user_ids: ids }))}
                label="Designate Admin Users (Optional)"
                placeholder="Search users by name or email..."
                showRole
                showOrganization
                excludeIds={users.filter(u => u.role === 'superadmin').map(u => u.id)}
                maxHeight={200}
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-ui-ink-3 bg-ui-hover rounded-md hover:bg-ui-line"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.name.trim()}
                className="px-4 py-2 bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary rounded-md disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganizationModal;